/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from 'react';
import { useDispatch, useStore } from 'react-redux';
import PropTypes from 'prop-types';
import { useRouter } from 'next/router';
import PreSubscribed from '@cont/PreSubscribed/PreSubscribed';
import Dynamic from '@base/Dynamic/Dynamic';
import { fetchPartPaymentStatus } from '@actions/checkout';
import useSubscribed from '@hooks/useSubscribed';
import SEO from '@seo/index';
import goalSchema from '@seo/goal/goal';
import { FREE_HOME_PAGE } from '@constants/segment';
import {
  fetchGoalInfo,
  fetchIconicSubscriptionInfo,
  fetchGoalBenefits,
  fetchGoalPageBlocks,
  fetchGoalFAQs,
  fetchGoalPageStaticContent,
  fetchPriceHikeInfo,
  fetchPlatformGoalBenefits
} from '@api/fetchers/goal';
import resolvePromiseToObject from '@utils/resolvePromiseToObj';
import useSelectedGoal, {
  useUpdateSelectedGoal
} from '@api/hooks/topology/useSelectedGoal';
import { getTopologyDataByUID } from '@selectors/topologies';
import { isUAIconsEnabled } from '@api/selectors/constants';
// SWR Hooks
import { SWRConfig } from 'swr';
import useFullGoalInfo from '@api/hooks/goal/useFullGoalInfo';
import { fetchTopologyInfo } from '@actions/topologies';
import { checkGoalSwitch, checkIsFollowedGoal } from '@cont/App/initialize';
import { eraseCookie, readCookie } from '@utils/cookies';
import {
  ICONS_GOAL_UID,
  UA_ICONS_SLUG
} from '@constants/unacademyIcons/icons.constants';
import fetchIsGoalGTP, {
  constantsAPIKey,
  fetchConstants
} from '@api/fetchers/constants';
import useLevelDisplayInfo from '@api/hooks/profile/useLevelDisplayInfo';
import { useToggleable } from '@stores/toggleables';
import { LOGIN_DRAWER } from '@constants/toggleables';
import useAccessControl from '@api/hooks/auth/useAccessControl';
import { getCheckPartPaymentStatus, getIsLoggedIn } from '@api/selectors/auth';
import useGroupJoinInvite from '@cont/Groups/groups-hooks/useGroupJoinInvite';
import PageReduxProvider from '@cont/App/PageReduxProviderWrapper';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import useFollowedGoals from '@api/hooks/goal/useFollowedGoals';
import { useIsMobile, usePageSeoContent } from '@stores/AppCommonData';
import useConstants from '@api/hooks/useConstants';
import fetchPageMetaInfo from '@api/fetchers/pageMetaInfo';
import { PLATFORM } from '@constants/index';
import FreeLayout from '@layout/FreeLayout/FreeLayout';

const GroupsJoinDialog = Dynamic(() =>
  import('@cont/Groups/WidgetCards/GroupsJoinDialog')
);

const PlusHomePage = ({
  goalUID,
  querySlug,
  // Pre-fetched data from the server
  fallbackData
}) => {
  const store = useStore();
  const {
    isIconicAvailable,
    isPlusAvailable,
    isK12Goal: isK12,
    isPlatformGoal
  } = fallbackData?.goalInfo || {};
  const { keys, goalInfo, faqs } = useFullGoalInfo({
    isK12,
    isPlatformGoal,
    goalUID,
    isIconicAvailable,
    fallbackData
  });

  const isLoggedIn = useMyInfo(getIsLoggedIn);
  const isSubscribed = useSubscribed(goalUID);
  const { accessControl } = useAccessControl(goalUID);
  const checkPartPaymentStatus = getCheckPartPaymentStatus(accessControl);
  const { mutate: fetchEducatorLevelDisplayInfo } = useLevelDisplayInfo();
  const [shouldJoinGroupViaInvite, setShouldJoinGroupViaInvite] =
    useState(false);
  const { groupJoinError } = useGroupJoinInvite(shouldJoinGroupViaInvite);

  const { slug: goalSlug, title, uid, ogImage, name } = goalInfo || {};

  const router = useRouter();

  const dispatch = useDispatch();

  const { selectedGoal } = useSelectedGoal();
  const selectedGoalCookie = readCookie('selectedGoalUid');
  const { followedGoals } = useFollowedGoals();
  useEffect(() => {
    if (isLoggedIn && followedGoals?.length)
      checkGoalSwitch(followedGoals, goalUID, selectedGoalCookie);
    if (selectedGoalCookie && selectedGoal && selectedGoalCookie !== goalUID)
      checkIsFollowedGoal(followedGoals, goalUID);
  }, [isLoggedIn, selectedGoalCookie, selectedGoal, followedGoals]);

  const accessToken = readCookie('accessToken');

  const setSelectedGoal = useUpdateSelectedGoal();

  useEffect(() => {
    if (!isPlusAvailable && !isPlatformGoal)
      router.replace(`/goal/${goalSlug}/${uid}/free-platform`);
    dispatch(fetchTopologyInfo(goalUID)); // Fetch topology info for compatibility with other components.
    fetchEducatorLevelDisplayInfo();

    if (!accessToken && selectedGoalCookie && uid !== selectedGoalCookie) {
      // If the user is logged out and already has a pre-selected goal cookie that isn't the current goal they're viewing, fetch that goal's info and let it be the selected one, show the user a prompt to change their selected goal.
      dispatch(fetchTopologyInfo(selectedGoalCookie)).then(() => {
        const selectedGoalData = getTopologyDataByUID(selectedGoalCookie)(
          store.getState()
        );
        if (selectedGoalData) {
          setSelectedGoal(selectedGoalCookie);
        } else eraseCookie('selectedGoalUid');
      });
    }
  }, []);

  useEffect(() => {
    // Slug Correction
    if (querySlug !== goalSlug)
      router.replace(
        '/goal/[goalSlug]/[goalUID]/home',
        `/goal/${goalSlug}/${uid}/home`,
        { shallow: true }
      );
  }, [router.asPath]);

  const { openElement: openLoginDrawer } = useToggleable(LOGIN_DRAWER);

  useEffect(() => {
    // Invites & Groups
    const { inviteeUID } = router.query;
    if (inviteeUID) {
      if (isLoggedIn && !isSubscribed) setShouldJoinGroupViaInvite(true);
      else if (!accessToken)
        // User doesn't have an active session
        openLoginDrawer();
    }
  }, [isLoggedIn, isSubscribed, goalUID, router.query, dispatch, accessToken]);

  useEffect(() => {
    if (isLoggedIn && checkPartPaymentStatus)
      dispatch(fetchPartPaymentStatus(goalUID));
  }, [isLoggedIn, dispatch, goalUID, checkPartPaymentStatus]);

  const isMobile = useIsMobile();
  useEffect(() => {
    if (isLoggedIn && isSubscribed && isPlusAvailable) {
      let redirectionURL = `/goal/${querySlug}/${goalUID}/planner`;
      if (!goalInfo?.isUaIconGoal) {
        if (isMobile) redirectionURL = `/goal/${querySlug}/${goalUID}/browse`;
      } else if (goalInfo?.isRecordedGoal) {
        redirectionURL = `/goal/${querySlug}/${goalUID}/browse`;
      } else redirectionURL = `/goal/${querySlug}/${goalUID}/icons-home`;
      router.push(redirectionURL);
    }
  }, [
    isMobile,
    goalSlug,
    goalUID,
    isLoggedIn,
    isSubscribed,
    querySlug,
    router,
    isPlusAvailable,
    goalInfo?.isUaIconGoal
  ]);

  const { constants } = useConstants();
  const isIconsEnabled = isUAIconsEnabled(constants);
  useEffect(() => {
    if (goalInfo?.isUaIconGoal && !!constants && !isIconsEnabled) {
      router.push('/explore');
    }
  }, [isIconsEnabled, goalInfo?.isUaIconGoal, constants, router]);

  const { title: pageSEOTitle, description: pageSEODescription } =
    usePageSeoContent();
  const goalTitle = `${pageSEOTitle || title || name || ''} | Unacademy`;
  const goalDescription =
    pageSEODescription ||
    `Prepare live from Top Educators with comprehensive, structured and crash courses along with practice section for ${
      pageSEOTitle || title || name || ''
    } on Unacademy Plus.`;
  const url = `https://unacademy.com/goal/${goalSlug}/${uid}`;
  const imageURL =
    ogImage ||
    'https://static.uacdn.net/static/dist/c76c2e18c7980818a913f9467c74c567.png';

  return (
    <SWRConfig
      value={{
        dedupingInterval: 60 * 1000,
        fallback: {
          [keys.goalInfoSWRKey]: fallbackData.goalInfo,
          [keys.iconicInfoSWRKey]: fallbackData.iconicSubscriptionInfo,
          [keys.goalBenefitsSWRKey]: fallbackData.goalBenefits,
          [keys.priceHikeSWRKey]: fallbackData.priceHikeInfo,
          [keys.metaInfoSWRKey]: fallbackData.metaInfo,
          [keys.staticContentSWRKey]: fallbackData.staticContent,
          [keys.faqSWRKey]: fallbackData.faqs,
          [keys.platformGoalBenefitsSWRKey]: fallbackData.platformGoalBenefits
        }
      }}
    >
      <SEO
        title={goalTitle}
        url={url}
        description={goalDescription}
        openGraph={{
          title: goalTitle,
          description: goalDescription,
          images: [
            {
              url: imageURL
            }
          ],
          url
        }}
        schema={goalSchema(
          faqs,
          title,
          url,
          fallbackData?.pageBlocks?.results || [],
          goalDescription
        )}
      />

      <PreSubscribed
        preSubscribedBlocks={fallbackData?.pageBlocks?.results || []}
        isSubscribed={isSubscribed}
        isK12={isK12}
        goalDetails={goalInfo}
        isPlatformGoal={isPlatformGoal}
      />

      {!!router.query.inviteeUID && !!groupJoinError && (
        <GroupsJoinDialog
          title={groupJoinError?.title}
          description={groupJoinError?.description}
          type="error"
          open={isLoggedIn && !!router.query.inviteeUID}
        />
      )}
    </SWRConfig>
  );
};

PlusHomePage.pageInfo = {
  isMWebSupported: true,
  name: FREE_HOME_PAGE,
  layoutOptions: {
    disableDarkMode: true
  }
};

export const getStaticProps = async ({ params, preview }) => {
  // Preview mode with getStaticProps ref: https://nextjs.org/docs/advanced-features/preview-mode#fetch-preview-data

  const { goalUID: routeGoalUID, goalSlug: querySlug } = params || {};
  if (!routeGoalUID || !querySlug) return { notFound: true };
  const goalUID = routeGoalUID.toUpperCase();
  try {
    const [goal, constants] = await Promise.all([
      fetchGoalInfo(goalUID),
      fetchConstants()
    ]);

    const isGTP = await fetchIsGoalGTP(goalUID, constants);
    if (isGTP)
      return {
        redirect: {
          destination: `/goal/gtp/${querySlug}/${goalUID}`,
          permanent: true
        }
      };

    if (goalUID === ICONS_GOAL_UID)
      return {
        redirect: {
          destination: `/goal/${UA_ICONS_SLUG}/${ICONS_GOAL_UID}`,
          permanent: false
        }
      };

    if (!goal || goal.errorCode) return { notFound: true, revalidate: 2 * 60 }; // Redirect user to 404 page.

    const goalSlug = goal.slug || querySlug;
    if (!goal.isPlusAvailable && !goal.isPlatformGoal)
      return {
        redirect: { destination: `/goal/${goalSlug}/${goalUID}/free-platform` }
      };

    const promises = [];
    const isIconicAvailable = goal?.isIconicAvailable;

    const { isK12Goal: isK12, isPlatformGoal } = goal;
    if (!isK12 && isIconicAvailable)
      promises.push(
        resolvePromiseToObject(
          fetchIconicSubscriptionInfo(`iconic-${goalUID}`),
          'iconicSubscriptionInfo'
        )
      );

    const goalBenefitsKey =
      goal && goal.isCatalogueFeatureAvailable
        ? 'doubts_and_solutions'
        : goalUID;
    if (isK12) {
      promises.push(
        resolvePromiseToObject(
          fetchGoalBenefits(goalBenefitsKey),
          'goalBenefits'
        )
      );
    }
    if (isPlatformGoal) {
      promises.push(
        resolvePromiseToObject(
          fetchPlatformGoalBenefits(goalUID),
          'platformGoalBenefits'
        )
      );
    }
    promises.push(
      resolvePromiseToObject(
        fetchGoalPageBlocks({ goalUID, platform: PLATFORM.OTHER }),
        'pageBlocks'
      )
    );
    promises.push(
      resolvePromiseToObject(
        fetchGoalPageBlocks({ goalUID, platform: PLATFORM.MOBILE }),
        'mobilePageBlocks'
      )
    );
    promises.push(resolvePromiseToObject(fetchGoalFAQs(), 'faqs'));
    promises.push(
      resolvePromiseToObject(
        fetchGoalPageStaticContent(goalUID),
        'staticContent'
      )
    );

    promises.push(
      resolvePromiseToObject(
        fetchPageMetaInfo(PlusHomePage.pageInfo.name, goalUID, preview),
        'metaInfo'
      )
    );
    promises.push(
      resolvePromiseToObject(fetchPriceHikeInfo(), 'priceHikeInfo')
    );

    let fallbackData = {};
    if (promises.length > 0)
      fallbackData = await Promise.all(promises).then((resolvedVals) => {
        const resultObj = {};
        for (let i = 0; i < resolvedVals.length; i += 1)
          resultObj[resolvedVals[i].type] = resolvedVals[i].result;
        return resultObj;
      });
    const genericBannerPageBlockInMobile =
      fallbackData?.mobilePageBlocks.results.find(
        ({ type }) => type === 'generic_banner_v2'
      );
    const genericBannerPageBlockInDesktop =
      fallbackData?.pageBlocks.results.find(
        ({ type }) => type === 'generic_banner_v2'
      );

    if (genericBannerPageBlockInDesktop) {
      genericBannerPageBlockInDesktop.data = {
        desktop: genericBannerPageBlockInDesktop?.data ?? null,
        mobile: genericBannerPageBlockInMobile?.data ?? null
      };
    } else if (genericBannerPageBlockInMobile) {
      genericBannerPageBlockInMobile.data = {
        desktop: genericBannerPageBlockInDesktop?.data ?? null,
        mobile: genericBannerPageBlockInMobile?.data ?? null
      };
      fallbackData.pageBlocks.results.push(genericBannerPageBlockInMobile);
    }

    fallbackData.goalInfo = goal;

    return {
      props: {
        goalUID,
        querySlug,
        pageMetaInfo: fallbackData.metaInfo,
        fallbackData,
        globalFallbackData: {
          [constantsAPIKey]: constants // pragma: allowlist secret
        },
        inPreviewMode: preview || false // Opt in to preview mode UI
      },
      revalidate: 5 * 60
    };
  } catch {
    return { notFound: true, revalidate: 2 * 60 };
  }
};

export const getStaticPaths = async () => {
  return {
    paths: [],
    fallback: 'blocking'
  };
};

export default PlusHomePage;

PlusHomePage.propTypes = {
  goalUID: PropTypes.string.isRequired,
  querySlug: PropTypes.string.isRequired
};
PlusHomePage.Layout = FreeLayout;
PlusHomePage.Wrapper = PageReduxProvider;
